import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { DuplicateGroupsTable } from "~/components/duplicates/duplicate-groups-table";
import { DuplicateStatsCards } from "~/components/duplicates/duplicate-stats";
import { useProjectContext } from "~/components/layout/project-context";
import { Button } from "~/components/ui/button";
import { $detectDuplicates } from "~/lib/duplicates/functions";
import { duplicateGroupsQueryOptions } from "~/lib/duplicates/queries";
import { projectCitationsQueryOptions } from "~/lib/projects/queries";
import { screeningSettingsQueryOptions } from "~/lib/screening/queries";

export const Route = createFileRoute("/_auth/projects/$projectId/duplicates/")({
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const { projectId } = params;

    await Promise.all([
      queryClient.ensureQueryData(projectCitationsQueryOptions(projectId)),
      queryClient.ensureQueryData(duplicateGroupsQueryOptions(projectId)),
      queryClient.ensureQueryData(screeningSettingsQueryOptions(projectId)),
    ]);
  },
  component: DuplicatesDashboard,
});

function DuplicatesDashboard() {
  const { projectId, projectTitle } = useProjectContext();
  const queryClient = useQueryClient();
  const { data: citations } = useSuspenseQuery(projectCitationsQueryOptions(projectId));
  const { data: groups } = useSuspenseQuery(duplicateGroupsQueryOptions(projectId));
  const { data: settings } = useSuspenseQuery(screeningSettingsQueryOptions(projectId));

  const isManualMode = settings.deduplicationMode === "manual";

  const stats = useMemo(() => {
    const pendingGroups = groups.filter((g) => g.status === "pending").length;
    const resolvedGroups = groups.filter((g) => g.status === "resolved").length;
    const duplicatesRemoved = citations.filter(
      (c) => c.duplicateStatus === "confirmed_duplicate",
    ).length;

    return {
      totalCitations: citations.length,
      pendingGroups,
      resolvedGroups,
      duplicatesRemoved,
    };
  }, [groups, citations]);

  const detectMutation = useMutation({
    mutationFn: () => $detectDuplicates({ data: { projectId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "citations"] });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "duplicates", "groups"],
      });
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "pipeline"] });
    },
  });

  const pendingGroups = groups.filter((g) => g.status === "pending");
  const resolvedGroups = groups.filter((g) => g.status === "resolved");

  // Derive detection result messaging
  const detectionMessage = useMemo(() => {
    if (!detectMutation.isSuccess) return null;
    const { citationsProcessed, duplicatesRemoved, groupsCreated } = detectMutation.data;

    if (citationsProcessed === 0) {
      return "All citations have already been processed.";
    }
    if (duplicatesRemoved > 0) {
      const verb = isManualMode ? "flagged" : "removed";
      return `Found ${groupsCreated} new duplicate group${groupsCreated !== 1 ? "s" : ""} (${duplicatesRemoved} duplicate${duplicatesRemoved !== 1 ? "s" : ""} ${verb})`;
    }
    return `Scanned ${citationsProcessed} citation${citationsProcessed !== 1 ? "s" : ""} — no new duplicates found.`;
  }, [detectMutation.isSuccess, detectMutation.data, isManualMode]);

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Duplicate Management</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {isManualMode
              ? "Detected duplicates need your review before being excluded from screening."
              : "Duplicates are automatically detected and removed. You can review and change the master citation if needed."}
          </p>
        </div>

        <div className="mb-6">
          <DuplicateStatsCards
            stats={stats}
            mode={isManualMode ? "manual" : "automatic"}
          />
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          {isManualMode ? (
            <Button
              render={
                <Link
                  to="/projects/$projectId/duplicates/review"
                  params={{ projectId }}
                  search={{ mode: "pending" }}
                />
              }
              disabled={pendingGroups.length === 0}
            >
              Review Pending Duplicates
              {pendingGroups.length > 0 && (
                <span className="bg-primary-foreground/20 ml-2 rounded-full px-2 py-0.5 text-xs">
                  {pendingGroups.length}
                </span>
              )}
            </Button>
          ) : (
            <Button
              render={
                <Link
                  to="/projects/$projectId/duplicates/review"
                  params={{ projectId }}
                  search={{ mode: "resolved" }}
                />
              }
              disabled={resolvedGroups.length === 0}
            >
              Review Resolved Groups
              {resolvedGroups.length > 0 && (
                <span className="bg-primary-foreground/20 ml-2 rounded-full px-2 py-0.5 text-xs">
                  {resolvedGroups.length}
                </span>
              )}
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => detectMutation.mutate()}
            disabled={detectMutation.isPending}
          >
            {detectMutation.isPending ? "Detecting..." : "Run Detection"}
          </Button>

          {detectMutation.isSuccess && detectionMessage && (
            <span className="text-sm text-green-600 dark:text-green-400">
              {detectionMessage}
            </span>
          )}

          {detectMutation.isError && (
            <span className="text-sm text-red-600 dark:text-red-400">
              Detection failed: {detectMutation.error.message}
            </span>
          )}
        </div>

        <div className="rounded-lg border">
          <div className="border-b px-4 py-3">
            <h3 className="font-semibold">
              All Duplicate Groups ({groups.length})
            </h3>
          </div>
          <DuplicateGroupsTable groups={groups} projectId={projectId} />
        </div>
      </main>
    </div>
  );
}
